/*
 * Copyright (c) 2026, FusionAuth, All Rights Reserved
 */

const launchUrl = 'https://fusionauth.link/help';
const launchTarget = 'fusionauth-help';
const HELP_LAUNCHER_INTRO_SEEN_KEY = 'helpLauncherIntroSeen';

// Matches the circle's own spin-in duration (see helpLauncherIntro in style.css) so the tooltip
// waits for the circle to finish animating before it shows for the first time.
const TOOLTIP_INITIAL_DELAY_MS = 1100;

// How long the tooltip stays visible during the automatic first-load showing before it fades out.
const TOOLTIP_VISIBLE_MS = 1200;

export class HelpLauncherService {
  #helpWindow: Window | null = null;

  constructor() {
    this.initialize();
  }

  initialize() {
    this.#initializeLauncher();
    this.#initializeTooltip();
  }

  #hasSeenIntroThisSession(): boolean {
    try {
      return window.sessionStorage.getItem(HELP_LAUNCHER_INTRO_SEEN_KEY) === 'true';
    } catch (_error) {
      return false;
    }
  }

  #markIntroSeenThisSession() {
    try {
      window.sessionStorage.setItem(HELP_LAUNCHER_INTRO_SEEN_KEY, 'true');
    } catch (_error) {
      // Ignore storage failures and keep default behavior.
    }
  }

  #open() {
    if (this.#helpWindow) {
      try {
        if (!this.#helpWindow.closed) {
          this.#helpWindow.focus();
          return;
        }
      } catch (_error) {
        // If the browser denies access after cross-origin navigation, treat it as stale.
      }

      this.#helpWindow = null;
    }

    // Probe for an already-open named window without navigating it.
    // window.open('', name) returns the existing window with that name (any origin) or opens a
    // fresh about:blank window when none exists — it never triggers a popup blocker because no
    // URL is loaded.
    const probe = window.open('', launchTarget);
    if (probe) {
      let isExisting = false;
      try {
        // Cross-origin windows throw on location access; same-origin blank windows do not.
        isExisting = probe.location.href !== 'about:blank';
      } catch (_error) {
        // Cross-origin means the window already has real content — it is the help window.
        isExisting = true;
      }

      if (isExisting) {
        // Reuse the already-open help window from a previous page load.
        this.#helpWindow = probe;
        probe.focus();
        return;
      }

      // Got a blank window — navigate it to the help URL in place.
      // Do NOT null out opener: Chrome clears window.name on cross-origin navigation when the
      // popup has no opener, which would prevent finding the window by name on later page loads.
      probe.location.href = launchUrl;
      this.#helpWindow = probe;
      return;
    }

    // Fallback: probe was blocked (rare). Open directly with the URL.
    const win = window.open(launchUrl, launchTarget);
    if (win) {
      this.#helpWindow = win;
    }
  }

  #initializeLauncher() {
    const helpLauncher = document.getElementById('help-launcher');
    if (!helpLauncher) {
      return;
    }

    if (this.#hasSeenIntroThisSession()) {
      helpLauncher.style.animation = 'none';
    }

    if (helpLauncher.dataset.helpBound === 'true') {
      return;
    }

    helpLauncher.dataset.helpBound = 'true';

    helpLauncher.addEventListener('click', event => {
      event.preventDefault();
      this.#open();
    });
  }

  #initializeTooltip() {
    const tooltip = document.getElementById('help-launcher-tooltip');
    const container = document.getElementById('help-launcher-container');
    if (!tooltip || !container) {
      return;
    }

    const tooltipElement = tooltip;
    const containerElement = container;

    if (tooltip.dataset.tooltipBound === 'true') {
      return;
    }

    tooltip.dataset.tooltipBound = 'true';

    let hideTimer: number | undefined;

    // Shows the tooltip, then fades it back out on its own after TOOLTIP_VISIBLE_MS.
    // Used only for the automatic first-load animation.
    const showThenFade = () => {
      tooltipElement.style.opacity = '1';

      if (hideTimer !== undefined) {
        window.clearTimeout(hideTimer);
      }

      hideTimer = window.setTimeout(() => {
        tooltipElement.style.opacity = '0';
        hideTimer = undefined;
      }, TOOLTIP_VISIBLE_MS);
    };

    // Shows the tooltip immediately and cancels any pending auto-hide.
    const show = () => {
      if (hideTimer !== undefined) {
        window.clearTimeout(hideTimer);
        hideTimer = undefined;
      }
      tooltipElement.style.opacity = '1';
    };

    // Hides the tooltip immediately.
    const hide = () => {
      tooltipElement.style.opacity = '0';
    };

    // Automatic first showing, timed to start right as the circle finishes spinning in.
    if (!this.#hasSeenIntroThisSession()) {
      window.setTimeout(showThenFade, TOOLTIP_INITIAL_DELAY_MS);
      this.#markIntroSeenThisSession();
    }

    // Hover: show while the pointer is over the container, hide when it leaves.
    containerElement.addEventListener('mouseenter', show);
    containerElement.addEventListener('mouseleave', hide);

    // Keyboard focus: show while focused, hide when focus moves away.
    // Ignore focusin when relatedTarget is null — that means the window itself just regained
    // focus (e.g. returning from the help window), not genuine in-page keyboard navigation.
    containerElement.addEventListener('focusin', (event: FocusEvent) => {
      if (event.relatedTarget !== null) {
        show();
      }
    });
    containerElement.addEventListener('focusout', hide);
  }
}

export const helpLauncherService = new HelpLauncherService();
